export type CashSaleVoidInput = {
  deviceSecret: string;
  sessionToken: string;
  orderId: string;
  reason: string;
  requestKey: string;
  clientContext?: Record<string, unknown>;
};

export type CashSaleVoidSyncResult = {
  ok: boolean;
  contract: "VOID_POS_CASH_SALE_V1";
  result_status: "VOIDED" | "IDEMPOTENT_REPLAY" | "ALREADY_VOIDED";
  idempotent_replay?: boolean;
  atomic?: boolean;
  event_id: string;
  order_id: string;
  order_no: string;
  order_status: "VOIDED";
  payment_id: string;
  payment_status: "VOIDED";
  currency_code: string;
  amount: number;
  shift_id: string;
  expected_cash: number | string;
  void_total: number | string;
  stock_reversal_count: number;
  stock_reversals: unknown[];
  reason: string;
  request_key: string;
  voided_at: string;
};

export type CashSaleVoidGatewayResult = {
  ok: boolean;
  data?: CashSaleVoidSyncResult;
  error?: string;
};

export class CashSaleVoidGatewayClient {
  private readonly baseUrl: string;
  private readonly timeoutMs: number;

  constructor(options: { baseUrl: string; timeoutMs?: number }) {
    this.baseUrl = options.baseUrl.replace(/\/+$/, "");
    this.timeoutMs = options.timeoutMs ?? 15_000;
  }

  async voidSale(input: CashSaleVoidInput): Promise<CashSaleVoidGatewayResult> {
    const controller = new AbortController();
    const timeout = window.setTimeout(() => controller.abort(), this.timeoutMs);

    try {
      const response = await fetch(`${this.baseUrl}/api/pos/runtime/cash-sale/void`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          device_secret: input.deviceSecret,
          session_token: input.sessionToken,
          order_id: input.orderId,
          reason: input.reason,
          request_key: input.requestKey,
          client_context: input.clientContext ?? {},
        }),
        signal: controller.signal,
        credentials: "omit",
      });

      const payload = (await response.json().catch(() => null)) as CashSaleVoidGatewayResult | null;
      if (!response.ok || !payload?.ok || !payload.data) {
        return { ok: false, error: payload?.error ?? `GATEWAY_HTTP_${response.status}` };
      }
      return payload;
    } catch (error) {
      return {
        ok: false,
        error:
          error instanceof DOMException && error.name === "AbortError"
            ? "GATEWAY_TIMEOUT"
            : "GATEWAY_UNREACHABLE",
      };
    } finally {
      window.clearTimeout(timeout);
    }
  }
}
